import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Search, TrendingUp, TrendingDown, AlertTriangle, Wallet, FileSpreadsheet, X, Check, Pencil, Boxes } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const rupiah = (n) => (n == null || n === "") ? "Rp 0" : ("Rp " + Number(n).toLocaleString("id-ID"));
const rupiahCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return "Rp " + (v / 1e9).toFixed(1).replace(".", ",") + " M";
  if (v >= 1e6) return "Rp " + (v / 1e6).toFixed(1).replace(".", ",") + " jt";
  return rupiah(v);
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthISO = () => new Date().toISOString().slice(0, 7);

/* ============== CASHFLOW TAB ============== */
export function CashflowTab() {
  const [coas, setCoas] = useState([]);
  const [txs, setTxs] = useState([]);
  const [sum, setSum] = useState(null);
  const [q, setQ] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [fType, setFType] = useState("");
  const [fCoa, setFCoa] = useState("");
  const [showCoa, setShowCoa] = useState(false);

  // Quick-add
  const [qDate, setQDate] = useState(todayISO());
  const [qType, setQType] = useState("expense");
  const [qCoa, setQCoa] = useState("");
  const [qAmt, setQAmt] = useState("");
  const [qPic, setQPic] = useState("");
  const [qDesc, setQDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [c, s] = await Promise.all([api.get("/coa"), api.get("/cashflow/summary")]);
    setCoas(c.data); setSum(s.data);
    reloadTx();
  };
  const reloadTx = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (fMonth) params.set("month", fMonth);
    if (fType) params.set("type", fType);
    if (fCoa) params.set("coa_id", fCoa);
    const r = await api.get(`/transactions?${params.toString()}`);
    setTxs(r.data);
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { const h = setTimeout(reloadTx, 250); return () => clearTimeout(h); }, [q, fMonth, fType, fCoa]);

  const coaMap = useMemo(() => Object.fromEntries(coas.map(c => [c.id, c])), [coas]);
  const availableCoas = coas.filter(c => c.type === qType && !c.archived);
  useEffect(() => { if (availableCoas.length && !availableCoas.find(c=>c.id===qCoa)) setQCoa(availableCoas[0].id); }, [qType, coas]);

  const submit = async () => {
    if (!qCoa || !qAmt) { toast.error("Pilih COA dan isi jumlah"); return; }
    setSaving(true);
    try {
      await api.post("/transactions", { date: qDate, type: qType, coa_id: qCoa, amount: Number(qAmt), pic: qPic, description: qDesc });
      setQAmt(""); setQPic(""); setQDesc("");
      toast.success("Transaksi tersimpan");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Hapus transaksi ini?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Terhapus"); loadAll();
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/report/donor", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-donatur-${todayISO()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal ekspor");
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KPI label="Dana Tersedia · Available Funds" value={rupiahCompact(sum?.balance)} sub={`Total masuk ${rupiahCompact(sum?.total_in)}`} tid="kpi-balance" />
        <KPI label="Masuk Bulan Ini · Income" value={rupiahCompact(sum?.in_this_month)} sub={changeLabel(sum?.in_this_month, sum?.in_prev_month)} icon={<TrendingUp className="w-3 h-3" />} tid="kpi-in" />
        <KPI label="Keluar Bulan Ini · Expense" value={rupiahCompact(sum?.out_this_month)} sub={changeLabel(sum?.out_this_month, sum?.out_prev_month)} icon={<TrendingDown className="w-3 h-3" />} tid="kpi-out" />
        <KPI label="% Bantuan Langsung · Direct Relief" value={`${sum?.program_pct ?? 0}%`} sub={`Operasional ${(100 - (sum?.program_pct ?? 0)).toFixed(1)}%`} tid="kpi-program" highlight />
      </div>

      {/* Top expense + alerts */}
      {(sum?.top_expense?.length || sum?.budget_alerts?.length) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sum?.top_expense?.length > 0 && (
            <div className="flat-border rounded-2xl bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Kategori Terbesar · Top Categories</div>
              <div className="space-y-2">
                {sum.top_expense.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate mr-2">{t.name}</span>
                    <span className="font-mono font-bold shrink-0">{rupiahCompact(t.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sum?.budget_alerts?.length > 0 && (
            <div className="flat-border rounded-2xl bg-amber-50 p-4">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-700 mb-2"><AlertTriangle className="w-3 h-3" /> Melewati Budget · Overspent</div>
              <div className="space-y-1.5">
                {sum.budget_alerts.map((a, i) => (
                  <div key={i} className="text-xs flex justify-between">
                    <span className="truncate mr-2">{a.name}</span>
                    <span className="font-mono">{rupiahCompact(a.spent)} / {rupiahCompact(a.budget)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Inline quick-add */}
      <div className="flat-border rounded-2xl bg-white p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#141414]/60 mb-2">Tambah Transaksi · Quick Add</div>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
          <input type="date" value={qDate} onChange={e=>setQDate(e.target.value)} data-testid="qa-date" className="col-span-2 md:col-span-2 flat-border rounded-lg px-2 py-2 text-sm font-mono outline-none" />
          <select value={qType} onChange={e=>setQType(e.target.value)} data-testid="qa-type" className="col-span-1 md:col-span-2 flat-border rounded-lg px-2 py-2 text-sm outline-none bg-white">
            <option value="expense">Keluar</option>
            <option value="income">Masuk</option>
          </select>
          <select value={qCoa} onChange={e=>setQCoa(e.target.value)} data-testid="qa-coa" className="col-span-2 md:col-span-3 flat-border rounded-lg px-2 py-2 text-sm outline-none bg-white">
            <option value="">— COA —</option>
            {availableCoas.map(c => <option key={c.id} value={c.id}>{c.name}{c.is_program ? " · program" : ""}</option>)}
          </select>
          <input type="number" min="0" value={qAmt} onChange={e=>setQAmt(e.target.value)} placeholder="Jumlah" data-testid="qa-amount" className="col-span-1 md:col-span-2 flat-border rounded-lg px-2 py-2 text-sm font-mono outline-none" />
          <input value={qPic} onChange={e=>setQPic(e.target.value)} placeholder="PIC / Kordinator" data-testid="qa-pic" className="col-span-2 md:col-span-2 flat-border rounded-lg px-2 py-2 text-sm outline-none" />
          <input value={qDesc} onChange={e=>setQDesc(e.target.value)} placeholder="Keterangan" data-testid="qa-desc" className="col-span-2 md:col-span-1 flat-border rounded-lg px-2 py-2 text-sm outline-none" />
        </div>
        <div className="mt-2 flex flex-wrap gap-2 items-center">
          <button onClick={submit} disabled={saving} data-testid="qa-save" className="flat-btn-primary rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> {saving ? "Menyimpan…" : "Tambah"}</button>
          <button onClick={()=>setShowCoa(v=>!v)} data-testid="coa-toggle" className="flat-btn bg-white rounded-full px-3 py-1.5 text-xs font-semibold">Kelola COA</button>
          <button onClick={exportXlsx} data-testid="export-report-btn" className="flat-btn bg-white rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ml-auto"><FileSpreadsheet className="w-3 h-3" /> Laporan Donatur (.xlsx)</button>
        </div>
      </div>

      {showCoa && <COAManager coas={coas} reload={loadAll} />}

      {/* Filter bar */}
      <div className="flat-border rounded-2xl bg-white p-3 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[180px] flat-border rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5" />
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cari keterangan / PIC..." data-testid="cf-search" className="flex-1 outline-none text-sm bg-transparent" />
        </div>
        <input type="month" value={fMonth} onChange={e=>setFMonth(e.target.value)} data-testid="cf-month" className="flat-border rounded-full px-3 py-1.5 text-xs font-mono outline-none" />
        <select value={fType} onChange={e=>setFType(e.target.value)} data-testid="cf-type" className="flat-border rounded-full px-3 py-1.5 text-xs outline-none bg-white">
          <option value="">Semua tipe</option>
          <option value="income">Masuk</option>
          <option value="expense">Keluar</option>
        </select>
        <select value={fCoa} onChange={e=>setFCoa(e.target.value)} data-testid="cf-coa" className="flat-border rounded-full px-3 py-1.5 text-xs outline-none bg-white">
          <option value="">Semua COA</option>
          {coas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(q || fMonth || fType || fCoa) && <button onClick={()=>{setQ("");setFMonth("");setFType("");setFCoa("");}} className="text-xs underline">reset</button>}
      </div>

      {/* Ledger */}
      <div className="flat-border rounded-2xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#fff0d2]">
            <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70">
              <th className="p-3">Tanggal</th>
              <th className="p-3">COA</th>
              <th className="p-3 hidden md:table-cell">PIC</th>
              <th className="p-3 hidden md:table-cell">Keterangan</th>
              <th className="p-3 text-right">Masuk</th>
              <th className="p-3 text-right">Keluar</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody data-testid="cf-tbody">
            {txs.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#141414]/50 text-sm">Belum ada transaksi.</td></tr>}
            {txs.map(t => {
              const c = coaMap[t.coa_id];
              return (
                <tr key={t.id} data-testid={`tx-${t.id}`} className="border-t border-[#141414]/10">
                  <td className="p-3 font-mono text-xs">{t.date}</td>
                  <td className="p-3">
                    <div className="font-semibold text-[13px]">{c?.name || "—"}</div>
                    {c?.is_program && <span className="text-[9px] font-bold uppercase tracking-wider text-[#670003]">program</span>}
                  </td>
                  <td className="p-3 hidden md:table-cell text-[13px]">{t.pic || <span className="opacity-40">—</span>}</td>
                  <td className="p-3 hidden md:table-cell text-[13px] max-w-[240px] truncate">{t.description || <span className="opacity-40">—</span>}</td>
                  <td className="p-3 text-right font-mono">{t.type === "income" ? rupiah(t.amount) : <span className="opacity-30">—</span>}</td>
                  <td className="p-3 text-right font-mono">{t.type === "expense" ? rupiah(t.amount) : <span className="opacity-30">—</span>}</td>
                  <td className="p-3 text-right"><button onClick={()=>del(t.id)} data-testid={`tx-del-${t.id}`} className="text-[color:var(--maroon)] p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, tid, highlight }) {
  return (
    <div data-testid={tid} className={`flat-border rounded-2xl p-4 ${highlight ? "bg-[#670003] text-[#fff0d2]" : "bg-white"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-[0.15em] leading-snug min-h-[2.4em] flex items-center gap-1 ${highlight ? "opacity-90" : "text-[#141414]/60"}`}>{icon}{label}</div>
      <div className="font-display text-2xl md:text-3xl leading-none mt-2 whitespace-nowrap">{value}</div>
      {sub && <div className={`text-[10px] mt-1.5 ${highlight ? "opacity-80" : "text-[#141414]/60"}`}>{sub}</div>}
    </div>
  );
}

function changeLabel(cur, prev) {
  if (!prev) return "—";
  const pct = ((cur || 0) - prev) / prev * 100;
  const s = pct >= 0 ? `↑ ${pct.toFixed(0)}%` : `↓ ${Math.abs(pct).toFixed(0)}%`;
  return `${s} vs bulan lalu`;
}

function COAManager({ coas, reload }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("expense");
  const [isProgram, setIsProgram] = useState(true);
  const [budget, setBudget] = useState("");

  const add = async () => {
    if (!name.trim()) { toast.error("Nama COA wajib"); return; }
    await api.post("/coa", { name: name.trim(), type, is_program: type === "expense" ? isProgram : false, budget_monthly: Number(budget || 0) });
    setName(""); setBudget("");
    toast.success("COA ditambah"); reload();
  };
  const del = async (id) => {
    if (!window.confirm("Hapus COA?")) return;
    await api.delete(`/coa/${id}`);
    toast.success("Terhapus / diarsipkan"); reload();
  };
  const patch = async (id, u) => {
    await api.put(`/coa/${id}`, u);
    reload();
  };

  return (
    <div className="flat-border rounded-2xl bg-white p-4">
      <div className="font-display text-lg mb-3">Chart of Accounts · Kategori Keuangan</div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4">
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Nama COA (mis. Bantuan Sembako)" data-testid="coa-new-name" className="col-span-5 flat-border rounded-lg px-3 py-2 text-sm outline-none" />
        <select value={type} onChange={e=>setType(e.target.value)} data-testid="coa-new-type" className="col-span-2 flat-border rounded-lg px-3 py-2 text-sm outline-none bg-white">
          <option value="expense">Keluar</option>
          <option value="income">Masuk</option>
        </select>
        {type === "expense" && (
          <label className="col-span-3 flat-border rounded-lg px-3 py-2 text-xs flex items-center gap-2 bg-white">
            <input type="checkbox" checked={isProgram} onChange={e=>setIsProgram(e.target.checked)} data-testid="coa-new-program" /> Bantuan Langsung (program)
          </label>
        )}
        <input type="number" min="0" value={budget} onChange={e=>setBudget(e.target.value)} placeholder="Budget/bln" data-testid="coa-new-budget" className="col-span-2 flat-border rounded-lg px-3 py-2 text-sm font-mono outline-none" />
        <button onClick={add} data-testid="coa-new-save" className="col-span-2 flat-btn-primary rounded-lg px-3 py-2 text-sm font-semibold"><Plus className="w-3 h-3 inline mr-1" /> Tambah</button>
      </div>
      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {coas.map(c => (
          <div key={c.id} data-testid={`coa-row-${c.id}`} className="flex items-center gap-2 flat-border rounded-lg px-3 py-1.5 text-sm">
            <span className="font-semibold flex-1 truncate">{c.name}</span>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${c.type === "income" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{c.type === "income" ? "masuk" : "keluar"}</span>
            {c.type === "expense" && (
              <label className="text-[10px] flex items-center gap-1">
                <input type="checkbox" checked={!!c.is_program} onChange={e=>patch(c.id, { is_program: e.target.checked })} data-testid={`coa-prog-${c.id}`} /> program
              </label>
            )}
            <input type="number" min="0" defaultValue={c.budget_monthly || 0} onBlur={e=>patch(c.id, { budget_monthly: Number(e.target.value) })} data-testid={`coa-budget-${c.id}`} className="w-24 flat-border rounded-md px-2 py-1 text-xs font-mono outline-none" placeholder="budget/bln" />
            {c.archived && <span className="text-[9px] text-[#141414]/50">arsip</span>}
            <button onClick={()=>del(c.id)} className="text-[color:var(--maroon)] p-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== INVENTORY TAB ============== */
export function InventoryTab() {
  const [items, setItems] = useState([]);
  const [moves, setMoves] = useState([]);
  const [sum, setSum] = useState(null);
  const [view, setView] = useState("items"); // items | moves
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("");
  const [modal, setModal] = useState(null); // 'in' | 'out' | 'item' | {edit:item}
  const [form, setForm] = useState({});

  const loadAll = async () => {
    const [i, s] = await Promise.all([api.get(`/inventory/items?q=${encodeURIComponent(q)}`), api.get("/inventory/summary")]);
    setItems(i.data); setSum(s.data);
    const params = new URLSearchParams(); if (q) params.set("q", q); if (fType) params.set("type", fType);
    const m = await api.get(`/inventory/movements?${params.toString()}`);
    setMoves(m.data);
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { const h = setTimeout(loadAll, 250); return () => clearTimeout(h); }, [q, fType]);

  const openModal = (kind, seed = {}) => { setModal(kind); setForm({ date: todayISO(), quantity: "", ...seed }); };
  const closeModal = () => { setModal(null); setForm({}); };

  const saveModal = async () => {
    try {
      if (modal === "item" || modal?.edit) {
        const payload = { name: form.name, sku: form.sku, unit: form.unit || "pcs", min_stock: Number(form.min_stock || 0) };
        if (modal?.edit) await api.put(`/inventory/items/${modal.edit.id}`, payload);
        else await api.post("/inventory/items", payload);
      } else {
        if (!form.item_id) { toast.error("Pilih barang"); return; }
        await api.post("/inventory/movements", { ...form, quantity: Number(form.quantity), type: modal });
      }
      toast.success("Tersimpan"); closeModal(); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Hapus / arsipkan barang?")) return;
    await api.delete(`/inventory/items/${id}`); toast.success("OK"); loadAll();
  };
  const delMove = async (id) => {
    if (!window.confirm("Hapus mutasi?")) return;
    await api.delete(`/inventory/movements/${id}`); toast.success("OK"); loadAll();
  };

  return (
    <div className="space-y-6">
      {/* Recap */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPI label="Total Jenis Barang · SKU" value={sum?.total_sku ?? 0} tid="inv-total-sku" />
        <KPI label="Total Stok · Total Qty" value={(sum?.total_qty ?? 0).toLocaleString("id-ID")} tid="inv-total-qty" />
        <KPI label="Hampir Habis · Low Stock" value={sum?.low_stock_count ?? 0} tid="inv-low" highlight={sum?.low_stock_count > 0} />
      </div>

      {/* Actions */}
      <div className="flat-border rounded-2xl bg-white p-3 flex flex-wrap gap-2 items-center">
        <button onClick={()=>openModal("in")} data-testid="inv-in-btn" className="flat-btn-primary rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Barang Masuk</button>
        <button onClick={()=>openModal("out")} data-testid="inv-out-btn" className="flat-btn bg-white rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Barang Keluar</button>
        <button onClick={()=>openModal("item")} data-testid="inv-add-item-btn" className="flat-btn bg-white rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1"><Boxes className="w-3 h-3" /> Tambah SKU</button>
        <div className="flex items-center gap-2 flex-1 min-w-[160px] flat-border rounded-full px-3 py-1.5 ml-auto">
          <Search className="w-3.5 h-3.5" />
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cari SKU / nama / PIC..." data-testid="inv-search" className="flex-1 outline-none text-sm bg-transparent" />
        </div>
      </div>

      {/* View toggle */}
      <div className="flex gap-2">
        <button onClick={()=>setView("items")} data-testid="inv-view-items" className={`rounded-full px-3 py-1 text-xs font-semibold border ${view === "items" ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "border-[color:var(--ink)]"}`}>Daftar Barang</button>
        <button onClick={()=>setView("moves")} data-testid="inv-view-moves" className={`rounded-full px-3 py-1 text-xs font-semibold border ${view === "moves" ? "bg-[color:var(--maroon)] text-[color:var(--cream)] border-[color:var(--maroon)]" : "border-[color:var(--ink)]"}`}>Mutasi</button>
        {view === "moves" && (
          <select value={fType} onChange={e=>setFType(e.target.value)} data-testid="inv-move-type" className="flat-border rounded-full px-3 py-1 text-xs outline-none bg-white ml-auto">
            <option value="">Semua tipe</option>
            <option value="in">Masuk</option>
            <option value="out">Keluar</option>
          </select>
        )}
      </div>

      {view === "items" ? (
        <div className="flat-border rounded-2xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fff0d2] text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70">
              <tr><th className="p-3">SKU</th><th className="p-3">Nama</th><th className="p-3">Satuan</th><th className="p-3 text-right">Stok</th><th className="p-3 text-right hidden md:table-cell">Min</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#141414]/50 text-sm">Belum ada barang.</td></tr>}
              {items.map(it => (
                <tr key={it.id} data-testid={`inv-item-${it.id}`} className={`border-t border-[#141414]/10 ${it.low_stock ? "bg-amber-50" : ""}`}>
                  <td className="p-3 font-mono text-xs">{it.sku || "—"}</td>
                  <td className="p-3 font-semibold">{it.name}</td>
                  <td className="p-3 text-xs">{it.unit}</td>
                  <td className="p-3 text-right font-mono font-bold">{it.qty_on_hand?.toLocaleString("id-ID")}</td>
                  <td className="p-3 text-right font-mono text-xs hidden md:table-cell">{it.min_stock?.toLocaleString("id-ID")}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={()=>{ setForm({ ...it }); setModal({ edit: it }); }} data-testid={`inv-edit-${it.id}`} className="p-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={()=>delItem(it.id)} data-testid={`inv-del-${it.id}`} className="p-1 text-[color:var(--maroon)]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flat-border rounded-2xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fff0d2] text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-[#141414]/70">
              <tr><th className="p-3">Tanggal</th><th className="p-3">Barang</th><th className="p-3">Tipe</th><th className="p-3 text-right">Jumlah</th><th className="p-3 hidden md:table-cell">PIC / Lokasi</th><th className="p-3 hidden md:table-cell">Catatan</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {moves.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#141414]/50 text-sm">Belum ada mutasi.</td></tr>}
              {moves.map(m => (
                <tr key={m.id} data-testid={`inv-move-${m.id}`} className="border-t border-[#141414]/10">
                  <td className="p-3 font-mono text-xs">{m.date}</td>
                  <td className="p-3"><div className="font-semibold text-[13px]">{m.item_name}</div><div className="text-[10px] opacity-60">{m.item_sku}</div></td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${m.type === "in" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{m.type === "in" ? "masuk" : "keluar"}</span></td>
                  <td className="p-3 text-right font-mono font-bold">{m.quantity?.toLocaleString("id-ID")} {m.item_unit}</td>
                  <td className="p-3 hidden md:table-cell text-[13px]">{m.pic || "—"}<div className="text-[10px] opacity-60">{m.location}</div></td>
                  <td className="p-3 hidden md:table-cell text-[13px] max-w-[220px] truncate">{m.note || "—"}</td>
                  <td className="p-3 text-right"><button onClick={()=>delMove(m.id)} data-testid={`inv-move-del-${m.id}`} className="text-[color:var(--maroon)] p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl flat-border-2 p-5 w-full max-w-md" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">{modal === "in" ? "Barang Masuk" : modal === "out" ? "Barang Keluar" : modal?.edit ? "Edit Barang" : "Tambah Barang / SKU"}</div>
              <button onClick={closeModal}><X className="w-4 h-4" /></button>
            </div>
            {(modal === "in" || modal === "out") ? (
              <div className="space-y-2">
                <select value={form.item_id || ""} onChange={e=>setForm({...form, item_id: e.target.value})} data-testid="mv-item" className="w-full flat-border rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">— Pilih Barang —</option>
                  {items.map(it => <option key={it.id} value={it.id}>{`${it.name} · ${it.sku} (stok ${it.qty_on_hand})`}</option>)}
                </select>
                <input type="date" value={form.date || todayISO()} onChange={e=>setForm({...form, date: e.target.value})} data-testid="mv-date" className="w-full flat-border rounded-lg px-3 py-2 text-sm font-mono" />
                <input type="number" min="0" value={form.quantity || ""} onChange={e=>setForm({...form, quantity: e.target.value})} placeholder="Jumlah" data-testid="mv-qty" className="w-full flat-border rounded-lg px-3 py-2 text-sm font-mono" />
                <input value={form.pic || ""} onChange={e=>setForm({...form, pic: e.target.value})} placeholder="PIC / Kordinator" data-testid="mv-pic" className="w-full flat-border rounded-lg px-3 py-2 text-sm" />
                <input value={form.location || ""} onChange={e=>setForm({...form, location: e.target.value})} placeholder="Lokasi (opsional)" data-testid="mv-loc" className="w-full flat-border rounded-lg px-3 py-2 text-sm" />
                <textarea value={form.note || ""} onChange={e=>setForm({...form, note: e.target.value})} placeholder="Catatan (opsional)" rows={2} data-testid="mv-note" className="w-full flat-border rounded-lg px-3 py-2 text-sm" />
              </div>
            ) : (
              <div className="space-y-2">
                <input value={form.sku || ""} onChange={e=>setForm({...form, sku: e.target.value})} placeholder="Kode SKU (mis. BRS-5KG)" data-testid="item-sku" className="w-full flat-border rounded-lg px-3 py-2 text-sm font-mono" />
                <input value={form.name || ""} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Nama barang" data-testid="item-name" className="w-full flat-border rounded-lg px-3 py-2 text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.unit || ""} onChange={e=>setForm({...form, unit: e.target.value})} placeholder="Satuan (pack, kg, botol)" data-testid="item-unit" className="w-full flat-border rounded-lg px-3 py-2 text-sm" />
                  <input type="number" min="0" value={form.min_stock || ""} onChange={e=>setForm({...form, min_stock: e.target.value})} placeholder="Min. stok (opsional)" data-testid="item-min" className="w-full flat-border rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={saveModal} data-testid="mv-save" className="flat-btn-primary rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Simpan</button>
              <button onClick={closeModal} className="flat-btn bg-white rounded-full px-4 py-2 text-sm font-semibold">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
